/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * ScenarioFieldsPanel.tsx — Scenari\1 \2ield\1 \2anel (services/operator-web-app).
 */

import { useWorkspace, useOperatorStore } from '@/store';
import { Panel, Input } from '@/components/ui';
import { HighlightBox } from '@/features/copilot';
import { ProvenanceBadge } from './ProvenanceBadge';
import { DEFAULT_SCENARIO_FIELD_IDS, SCENARIO_FIELD_I18N_KEYS } from './customerCardTypes';
import type { FieldSource } from '@/store';
import { useTranslation } from '@/i18n';

/**
 * L2 — Scenario fields with inline provenance. Plan: 05, 14 wireframes.
 * Inline validation: error prop can be set per field (future).
 */
export function ScenarioFieldsPanel() {
  const t = useTranslation();
  const workspace = useWorkspace();
  const setScenarioField = useOperatorStore((s) => s.setScenarioField);
  const scenarioFields = workspace.scenarioFields;
  const wrapUpRequired = new Set(workspace.wrapUpRequiredFields);

  const handleChange = (fieldId: string, value: string, source: FieldSource = 'manual') => {
    setScenarioField(fieldId, value, source);
  };

  return (
    <HighlightBox widgetId="customer_card_fields">
      <Panel title={t('scenario.title')} padding="md">
        <div className="space-y-3">
          {DEFAULT_SCENARIO_FIELD_IDS.map((fieldId) => {
            const entry = scenarioFields[fieldId];
            const value = entry?.value ?? '';
            const source = entry?.source ?? 'manual';
            const required = wrapUpRequired.has(fieldId);
            const label = t(SCENARIO_FIELD_I18N_KEYS[fieldId] ?? fieldId);
            return (
              <div key={fieldId} className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {label}
                    {required && (
                      <span className="text-red-500 ml-0.5" aria-hidden>
                        *
                      </span>
                    )}
                  </label>
                  <ProvenanceBadge source={source} />
                </div>
                <Input
                  value={value}
                  onChange={(e) => handleChange(fieldId, e.target.value, 'manual')}
                  placeholder={t('scenario.enter_field', { field: label.toLowerCase() })}
                  inputClassName="text-sm"
                />
              </div>
            );
          })}
        </div>
      </Panel>
    </HighlightBox>
  );
}
